import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterLink, RouterLinkActive, RouterModule, provideRouter } from '@angular/router';
import { AuthClientConfig, AuthHttpInterceptor, authHttpInterceptorFn, AuthModule, provideAuth0 } from '@auth0/auth0-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { routes } from './app-routing.module';
import { AppComponent } from './app.component';
import { MessagesComponent } from './components/messages/messages.component';
import { NavBarComponent } from './components/nav-bar/nav-bar.component';
import { AccountModule } from './pages/account/account.module';
import { AllocationModule } from './pages/allocation/allocation.module';
import { PositionModule } from './pages/position/position.module';
import { ProfileModule } from './pages/profile/profile.module';
import { APP_CONFIG, DEFAULT_APP_CONFIG } from './shared/app.config';
import { ApiService } from './shared/services/api.service';
import { AppConfigService } from './shared/services/app-config.service';
import { SharedModule } from './shared/shared.module';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { TokenService } from './shared/services/token.service';
import { createAuthEnvironment } from './shared/models/auth.config';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';

// Provide an initializer function that returns a Promise
function configInitializer(
  handler: HttpBackend,
  config: AuthClientConfig,
  appConfigService: AppConfigService
) {
  return () =>
    new HttpClient(handler)
      .get('/assets/config.json')
      .toPromise()
      .then((loadedConfig: any) => {
        const env = createAuthEnvironment(loadedConfig);
        appConfigService.setConfig(loadedConfig);
        config.set({
          domain: env.auth.domain,
          clientId: env.auth.clientId,
          authorizationParams: env.auth.authorizationParams,
          errorPath: env.auth.errorPath,
          // Add these options to prevent iframe/popup issues
          useRefreshTokens: false,
          useRefreshTokensFallback: false,
          // Ensure redirect mode is used (not popup)
          cacheLocation: 'localstorage' as any,
          httpInterceptor: env.httpInterceptor
        });
      });
}

@NgModule({
  declarations: [
    AppComponent,
    MessagesComponent,
    NavBarComponent,
  ],
  imports: [
    AccountModule,
    AllocationModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    FontAwesomeModule,
    FormsModule,
    HttpClientModule,
    PositionModule,
    ProfileModule,
    ReactiveFormsModule,
    RouterLink,
    RouterLinkActive,
    RouterModule,
    SharedModule,
    AuthModule.forRoot(), 
  ],
  exports: [
    SharedModule
  ],
  providers: [
    { provide: APP_CONFIG, useValue: DEFAULT_APP_CONFIG },
    ApiService,
    TokenService,
    AppConfigService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializer,
      deps: [HttpBackend, AuthClientConfig, AppConfigService],
      multi: true,
    },

    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideRouter(routes),
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
